//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//* Import PaginationData type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import { Tag, TagsDetails, TagResponse } from "../tags/types/types";

// API methods for Tag operations
export const tagAPI = {
  // Fetch all Tags with optional search and pagination
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<TagResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/tags?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          tags: data.tags || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching tags:", error);
        throw new Error("Failed to fetch tags");
      });
  },

  // Fetch a Tag by ID
  getById: async function (id: string, token: string): Promise<TagsDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/tags/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Error fetching Tag:", error);
        throw new Error("Failed to fetch Tag");
      });
  },

  // Create a new Tag
  create: async function (
    data: { name: string; color: string },
    token: string
  ): Promise<Tag> {
    return api
      .request({
        url: `${API_URI}/admin/private/tags`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating Tag:", error, data);
        throw new Error("Failed to create Tag");
      });
  },

  // Update an existing Tag
  update: async function (
    id: string,
    data: { name: string; color: string },
    token: string
  ): Promise<Tag> {
    return api
      .request({
        url: `${API_URI}/admin/private/tags/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating Tag:", error);
        throw new Error("Failed to update Tag");
      });
  },

  // Delete an Tag by ID
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/tags/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting Tag:", error);
        throw new Error("Failed to delete Tag");
      });
  },
};
